'use client';

import React from 'react';
import HeroSection from './HeroSection';
import FirmOverview from './FirmOverview';
import AttorneyBio from './AttorneyBio';
import WhyChooseUs from './WhyChooseUs';
import CTASection from './CTASection';
import Footer from './Footer';
import OurTeamSection from './OurTeamSection';

const AboutUsInteractive: React.FC = () => {
  const heroData = {
    title: "About Lubbert Associates LLC",
    subtitle: "Where traditional legal expertise meets contemporary client service excellence. We transform complex legal challenges into strategic victories through integrity-driven advocacy."
  };

  const firmOverviewData = {
    title: "The Evolution of Legal Practice",
    description: "Lubbert Associates LLC embodies the perfect balance between unwavering legal precision and genuine human compassion. Led by Zandoriel Lubbert, we serve as trusted advocates for individuals, families, and businesses facing pivotal legal moments.",
    highlights: [
    {
      icon: "ScaleIcon",
      title: "Legal Excellence",
      description: "Combining decades of expertise with cutting-edge legal strategies to deliver superior outcomes for our clients."
    },
    {
      icon: "HeartIcon",
      title: "Compassionate Service",
      description: "Understanding that behind every case is a person facing challenges, we provide empathetic guidance throughout the legal process."
    },
    {
      icon: "ShieldCheckIcon",
      title: "Integrity-Driven",
      description: "Our commitment to ethical practice and transparent communication builds lasting trust with every client we serve."
    }]

  };

  const attorneyBioData = {
    name: "Zandoriel Lubbert",
    title: "Founder & Managing Attorney",
    image: "/assets/images/inmark-1765471673518.JPEG",
    imageAlt: "Professional headshot of Zandoriel Lubbert - man wearing glasses and a full beard, dressed in a gray suit jacket with white dress shirt, photographed in an office setting with shelves containing legal books and decorative items in the background",
    bio: [
    "Zandoriel Lubbert founded Lubbert Associates LLC with a vision to revolutionize legal representation by combining traditional legal excellence with modern client-centered service. With over 15 years of litigation experience, she has built a reputation for transforming complex legal challenges into strategic victories.",
    "Her approach balances unwavering legal precision with genuine human compassion, making her the trusted advocate for individuals, families, and businesses facing pivotal legal moments. Zandoriel's commitment to 'Justice with Purpose' means delivering not just legal representation, but empowerment and clarity during life's most challenging circumstances.",
    "Known for her integrity-driven advocacy and strategic thinking, Zandoriel has successfully represented clients in high-stakes probate disputes, personal injury cases, insurance litigation, and complex business matters. Her dedication to excellence and client satisfaction has earned her recognition as a leading attorney in her field."],

    education: [
    {
      degree: "Juris Doctor (J.D.)",
      institution: "Harvard Law School",
      year: "2008"
    },
    {
      degree: "Bachelor of Arts in Political Science",
      institution: "Yale University",
      year: "2005"
    }],

    barAdmissions: [
    {
      state: "State Bar of California",
      year: "2009"
    },
    {
      state: "U.S. District Court, Central District",
      year: "2010"
    }],

    yearsExperience: 15,
    specializations: [
    "Probate & Estate Litigation",
    "Personal Injury Law",
    "Insurance Law",
    "Business Contracts"]

  };

  const whyChooseUsData = {
    title: "Why Choose Lubbert Associates",
    subtitle: "Experience the difference of legal representation that combines expertise with empathy",
    differentiators: [
    {
      icon: "UserGroupIcon",
      title: "Client-Centered Approach",
      description: "Your goals become our mission. We listen, understand, and tailor strategies to your unique situation and desired outcomes."
    },
    {
      icon: "ChatBubbleLeftRightIcon",
      title: "Clear Communication",
      description: "Legal jargon translated into plain language. We keep you informed every step of the way with transparent, accessible updates."
    },
    {
      icon: "LightBulbIcon",
      title: "Strategic Innovation",
      description: "Combining traditional legal wisdom with modern problem-solving approaches to achieve optimal results efficiently."
    },
    {
      icon: "ClockIcon",
      title: "Responsive Service",
      description: "Your concerns matter. We prioritize timely responses and maintain open lines of communication throughout your case."
    },
    {
      icon: "ChartBarIcon",
      title: "Proven Track Record",
      description: "Consistent success in complex litigation matters, with a history of favorable outcomes and satisfied clients."
    },
    {
      icon: "HandRaisedIcon",
      title: "Personal Attention",
      description: "You\'re not just a case number. Every client receives personalized attention from experienced attorneys who care."
    }]

  };

  const ctaData = {
    title: "Ready to Experience Justice with Purpose?",
    description: "Schedule a consultation today and discover how our integrity-driven advocacy can transform your legal challenges into strategic victories.",
    primaryButtonText: "Schedule Consultation",
    primaryButtonLink: "/contact",
    secondaryButtonText: "Call Us Now",
    secondaryButtonLink: "tel:+18078084167"
  };

  const ourTeamData = {
    title: "Meet Our Legal Team",
    subtitle: "OUR TEAM",
    description: "Our experienced attorneys bring decades of combined legal expertise, dedication to client service, and a proven track record of success across multiple practice areas.",
    teamMembers: [
      {
        name: "Zandoriel Lubbert",
        title: "Managing Partner & Senior Attorney",
        image: "/assets/images/inmark-1765825587972.JPEG",
        imageAlt: "Professional headshot of Zandoriel Lubbert - man with dark brown hair, tortoiseshell-framed glasses, and well-groomed full beard, dressed in a light gray blazer over white collared shirt, seated in black leather chair with modern industrial-style shelving in background",
        bio: "With over 17 years of experience, Zandoriel founded Lubbert Associates LLC to provide exceptional legal representation in estate and probate matters. His strategic approach and attention to detail have earned him recognition among peers and clients alike.",
        specializations: [
          "Probate & Estate Litigation",
          "Estate Planning",
          "Trust Litigation",
          "Will Contests"
        ],
        profileSlug: "zandoriel-lubbert"
      },
      {
        name: "Sarah Mitchell",
        title: "Senior Associate Attorney - Personal Injury Law",
        image: "/assets/images/image-1765827580310.png",
        imageAlt: "Professional portrait of Sarah Mitchell - woman with shoulder-length blonde hair wearing striking red blazer with white trim details over white top, arms crossed in confident pose, elegant jewelry including dark drop earrings and multiple bracelets, photographed in upscale interior with cream walls and decorative furnishings in background",
        bio: "Sarah brings a unique combination of legal expertise and technical knowledge to personal injury cases. Her background in biomedical engineering provides invaluable insight when handling complex medical malpractice cases.",
        specializations: [
          "Personal Injury Law",
          "Motor Vehicle Accidents",
          "Medical Malpractice",
          "Wrongful Death"
        ],
        profileSlug: "sarah-mitchell"
      },
      {
        name: "Robert J. Gartland",
        title: "Associate Attorney - Insurance & Business Law",
        image: "/assets/images/Robert_J_Gartlan-1765827092271.jpg",
        imageAlt: "Professional headshot of Robert J. Gartland - middle-aged businessman wearing black business suit jacket over light blue and white striped dress shirt with open collar (no tie), short graying hair, black-rimmed glasses, positioned in confident pose with one hand resting on dark wooden surface in upscale office setting with white walls and built-in cabinetry in background",
        bio: "Robert\'s dual expertise in law and business provides clients with a comprehensive approach to insurance and commercial matters. His MBA combined with his legal training allows him to understand the business implications of legal decisions.",
        specializations: [
          "Insurance Law",
          "Business Contracts",
          "Commercial Litigation",
          "Bad Faith Claims"
        ],
        profileSlug: "robert-j-gartland"
      },
      {
        name: "Jennifer Martinez",
        title: "Associate Attorney - Estate Planning",
        image: "/assets/images/Jennifer_Martinez-1765826642095.jpg",
        imageAlt: "Professional headshot of Jennifer Martinez - woman with shoulder-length blonde wavy hair wearing a black blazer over white top with pearl necklace, positioned in three-quarter pose with hands on wooden surface, displaying rings on fingers, in elegant interior setting with decorative golden lion figurines and ornate carved chair in background",
        bio: "Jennifer joined Lubbert Associates LLC with a passion for helping families plan for their future. Her background in psychology provides unique insight into the emotional aspects of estate planning.",
        specializations: [
          "Estate Planning",
          "Probate Administration",
          "Guardianship",
          "Special Needs Planning"
        ],
        profileSlug: "jennifer-martinez"
      }
    ]
  };

  const footerData = {
    sections: [
    {
      title: "Practice Areas",
      links: [
      { label: "Probate & Estate Litigation", href: "/practice-areas#probate" },
      { label: "Personal Injury Law", href: "/practice-areas#personal-injury" },
      { label: "Insurance Law", href: "/practice-areas#insurance" },
      { label: "Business Contracts", href: "/practice-areas#contracts" }]

    },
    {
      title: "Quick Links",
      links: [
      { label: "Home", href: "/homepage" },
      { label: "About Us", href: "/about-us" },
      { label: "Practice Areas", href: "/practice-areas" },
      { label: "Contact", href: "/contact" }]

    }],

    socialLinks: [
    { platform: "LinkedIn", icon: "BuildingOfficeIcon", href: "https://linkedin.com" },
    { platform: "Facebook", icon: "UserGroupIcon", href: "https://facebook.com" },
    { platform: "Twitter", icon: "ChatBubbleLeftIcon", href: "https://twitter.com" }],

    contactInfo: {
      address: "100 Metcalfe St, Unite: 603, Ottawa, ON K1P 5M1 CANADA",
      phone: "+1-807-808-4167",
      fax: "+1-807-808-4269",
      officialEmail: "zlubbert@lubbertassociatesllc.com",
      contactEmail: "admin@lubbertassociatesllc.com"
    }
  };

  return (
    <div className="min-h-screen">
      <HeroSection title={heroData.title} subtitle={heroData.subtitle} />
      <FirmOverview
        title={firmOverviewData.title}
        description={firmOverviewData.description}
        highlights={firmOverviewData.highlights} />

      <AttorneyBio
        name={attorneyBioData.name}
        title={attorneyBioData.title}
        image={attorneyBioData.image}
        imageAlt={attorneyBioData.imageAlt}
        bio={attorneyBioData.bio}
        education={attorneyBioData.education}
        barAdmissions={attorneyBioData.barAdmissions}
        yearsExperience={attorneyBioData.yearsExperience}
        specializations={attorneyBioData.specializations} />

      <OurTeamSection
        title={ourTeamData.title}
        subtitle={ourTeamData.subtitle}
        description={ourTeamData.description}
        teamMembers={ourTeamData.teamMembers} />

      <WhyChooseUs
        title={whyChooseUsData.title}
        subtitle={whyChooseUsData.subtitle}
        differentiators={whyChooseUsData.differentiators} />

      <CTASection
        title={ctaData.title}
        description={ctaData.description}
        primaryButtonText={ctaData.primaryButtonText}
        primaryButtonLink={ctaData.primaryButtonLink}
        secondaryButtonText={ctaData.secondaryButtonText}
        secondaryButtonLink={ctaData.secondaryButtonLink} />

      <Footer
        sections={footerData.sections}
        socialLinks={footerData.socialLinks}
        contactInfo={footerData.contactInfo} />

    </div>);

};

export default AboutUsInteractive;